export const ENTITY_FLIGHT_EVIDENCE_TYPES = ['single_match', 'ambiguous', 'likely'] as const;
export type EntityFlightEvidenceType = (typeof ENTITY_FLIGHT_EVIDENCE_TYPES)[number];

export const ENTITY_FLIGHT_EVIDENCE_LABELS: Record<EntityFlightEvidenceType, string> = {
  single_match: 'Single aircraft match',
  ambiguous: 'Multiple candidate aircraft',
  likely: 'Likely air travel'
};

// aircraft: a candidate aircraft on the journey looks military. airfield: an endpoint is at a military-only airfield.
export const ENTITY_FLIGHT_MILITARY_TYPES = ['aircraft', 'airfield'] as const;
export type EntityFlightMilitaryType = (typeof ENTITY_FLIGHT_MILITARY_TYPES)[number];

export const ENTITY_FLIGHT_MILITARY_LABELS: Record<EntityFlightMilitaryType, string> = {
  aircraft: 'Flew on military aircraft',
  airfield: 'Travelled via military airfield'
};

// Why an aircraft was flagged military, as the API names it.
export const ENTITY_FLIGHT_MILITARY_SIGNAL_LABELS: Record<string, string> = {
  us_address_block: 'US military transponder address',
  registration: 'military-format registration',
  callsign: 'military operator callsign',
  aircraft_type: 'military aircraft type'
};

export type EntityFlightSource = 'BB' | 'QQ' | 'CC';

export interface EntityFlightSearchRequest {
  advertiser_ids: string[];
  start_at: string;
  end_at: string;
  evidence_types?: EntityFlightEvidenceType[];
  sources?: EntityFlightSource[];
  military_types?: EntityFlightMilitaryType[];
}

export interface EntityFlightSummary {
  advertiser_id: string;
  journey_count: number;
  single_match_count: number;
  ambiguous_count: number;
  likely_count: number;
  // 0 unless the request named military_types.
  military_aircraft_count: number;
  military_airfield_count: number;
}

export interface EntityFlightLocation {
  latitude: number | null;
  longitude: number | null;
  name: string | null;
  iata: string | null;
  device_distance_km: number | null;
  device_on_airport: boolean | null;
}

export interface EntityFlightCandidate {
  rank: number;
  status: 'confirmed' | 'alternative';
  icao: string;
  registration: string | null;
  callsign: string | null;
  aircraft_type: string | null;
  takeoff_at: string;
  landing_at: string;
  origin: EntityFlightLocation;
  destination: EntityFlightLocation;
  origin_error_km: number | null;
  destination_error_km: number | null;
  track_residual_km: number | null;
  resolved_by: string | null;
  data_complete: boolean | null;
  landing_inferred: boolean | null;
  max_ping_gap_minutes: number | null;
  // Inferred from the aircraft's identity (state aircraft included); null when the run predates the signal.
  military_aircraft?: boolean | null;
  military_aircraft_signals?: string[];
}

export interface EntityFlightJourney {
  event_date: string;
  source: EntityFlightSource;
  advertiser_id: string;
  journey_id: string;
  run_id: string;
  evidence_type: EntityFlightEvidenceType;
  ambiguity_type: 'unique' | 'same_route' | 'divergent' | 'track_resolved' | 'unmatched';
  candidate_count: number;
  device_departure_at: string;
  device_arrival_at: string;
  device_departure: { latitude: number; longitude: number };
  device_arrival: { latitude: number; longitude: number };
  route_origin?: EntityFlightLocation;
  route_destination?: EntityFlightLocation;
  // Military-only airfield within ~1 km of the device endpoint: location evidence, not a military flight.
  origin_military_airfield?: string | null;
  destination_military_airfield?: string | null;
  distance_km: number;
  speed_kmh: number;
  primary_candidate: EntityFlightCandidate | null;
  alternatives: EntityFlightCandidate[];
}

export interface EntityFlightJourneyResponse {
  advertiser_id: string;
  start_at: string;
  end_at: string;
  journeys: EntityFlightJourney[];
  pagination: { limit: number; next_cursor: string | null };
}

export interface EntityFlightJourneyRequest {
  advertiser_id: string;
  start_at: string;
  end_at: string;
  evidence_types?: EntityFlightEvidenceType[];
  sources?: EntityFlightSource[];
  limit?: number;
  cursor?: string;
}

export interface EntityFlightRunStatus {
  run_id: string;
  input_date: string;
  sources: EntityFlightSource[];
  algorithm_version: string;
  published_at: string;
  match_row_count: number;
  journey_count: number;
}

export interface EntityFlightStatusResponse {
  available: boolean;
  coverage: { start_at: string; end_at: string } | null;
  sources: EntityFlightSource[];
  published_run_count: number;
  latest_run: EntityFlightRunStatus | null;
}

export type EntityFlightRouteCoordinates = [[number, number], [number, number]];

export const getEntityFlightJourneyId = (journey: EntityFlightJourney) => [journey.source, journey.run_id, journey.event_date, journey.journey_id].join(':');

export const getEntityFlightRouteCoordinates = (journey: EntityFlightJourney): EntityFlightRouteCoordinates => {
  const origin = journey.route_origin ?? journey.primary_candidate?.origin;
  const destination = journey.route_destination ?? journey.primary_candidate?.destination;
  if (origin?.longitude != null && origin.latitude != null && destination?.longitude != null && destination.latitude != null) {
    return [
      [origin.longitude, origin.latitude],
      [destination.longitude, destination.latitude]
    ];
  }

  return [
    [journey.device_departure.longitude, journey.device_departure.latitude],
    [journey.device_arrival.longitude, journey.device_arrival.latitude]
  ];
};

// Reverse lookup: which entities were matched to an aircraft's flight legs.
// Legs are identified by ICAO and exact takeoff time, which the matcher copies from the ADS-B leg.
export interface EntityFlightAircraftLegs {
  icao: string;
  // Echoed back so results can be keyed by the ADS-B audience identity; not used for matching.
  callsign: string | null;
  takeoff_at: string[];
}

export interface EntityFlightAircraftSearchRequest {
  aircraft: EntityFlightAircraftLegs[];
  sources?: EntityFlightSource[];
}

// An entity confirmed on any of the counted legs is counted as confirmed; alternative_count only has entities that are never confirmed.
export interface EntityFlightPassengerCounts {
  confirmed_count: number;
  alternative_count: number;
}

export interface EntityFlightAircraftLegSummary extends EntityFlightPassengerCounts {
  takeoff_at: string;
}

export interface EntityFlightAircraftSummary extends EntityFlightPassengerCounts {
  icao: string;
  callsign: string | null;
  legs: EntityFlightAircraftLegSummary[];
}

// Dates the matcher has published; legs outside it have not been matched yet.
export type EntityFlightCoverage = {
  start_at: string;
  end_at: string;
} | null;

export interface EntityFlightAircraftSearchResponse {
  coverage: EntityFlightCoverage;
  aircraft: EntityFlightAircraftSummary[];
}

export interface EntityFlightPassengersRequest {
  icao: string;
  takeoff_at: string[];
  sources?: EntityFlightSource[];
}

export interface EntityFlightPassenger {
  source: EntityFlightSource;
  advertiser_id: string;
  journey_id: string;
  event_date: string;
  status: 'confirmed' | 'alternative';
  rank: number;
  candidate_count: number;
  evidence_type: EntityFlightEvidenceType;
  ambiguity_type: EntityFlightJourney['ambiguity_type'];
  device_departure_at: string;
  device_arrival_at: string;
}

export interface EntityFlightPassengerLeg {
  takeoff_at: string;
  passengers: EntityFlightPassenger[];
}

export interface EntityFlightPassengersResponse {
  coverage: EntityFlightCoverage;
  icao: string;
  legs: EntityFlightPassengerLeg[];
}

export const MAX_ENTITY_FLIGHT_AIRCRAFT_LEGS = 20_000;
// Mirrors the service's address check so one malformed ICAO can't fail the whole count request.
const ADSB_ICAO_PATTERN = /^~?[0-9a-f]{6}$/;

export const isEntityFlightIcao = (icao: string | null | undefined): icao is string => !!icao && ADSB_ICAO_PATTERN.test(icao.toLowerCase());

// The service lowercases ICAOs, so lookups key on the lowercase form.
export const getEntityFlightAircraftKey = (icao: string, callsign: string | null) => `${icao.toLowerCase()}|${callsign ?? ''}`;

export const isEntityFlightCovered = (coverage: EntityFlightCoverage, takeoffAt: number) => {
  if (!coverage) return false;
  const takeoffDate = new Date(takeoffAt).toISOString().slice(0, 10);
  return takeoffDate >= coverage.start_at && takeoffDate <= coverage.end_at;
};
